import { Card } from '@/components/ui/card'

export interface CollectionMonth {
  starts_on: string
  label: string
  amount: string
  amount_cents: number
}

interface CollectionsChartProps {
  months: CollectionMonth[]
}

const CHART_WIDTH = 1040
const PLOT_TOP = 24
const PLOT_BOTTOM = 148
const LABEL_Y = 172

export function CollectionsChart({ months }: CollectionsChartProps) {
  const empty = months.every(({ amount_cents }) => amount_cents === 0)

  return (
    <Card className="p-4">
      <div className="flex items-baseline gap-2">
        <h2 className="text-body font-medium text-foreground">Collected</h2>
        <span className="text-caption text-muted-foreground">Last 12 months</span>
      </div>
      <div className="mt-3">
        <svg
          aria-label={
            empty ? 'No collections in the last 12 months' : 'Collections in the last 12 months'
          }
          className="block w-full"
          height="180"
          role="img"
          viewBox={`0 0 ${CHART_WIDTH} 180`}
        >
          <ChartContents months={months} empty={empty} />
        </svg>
      </div>
    </Card>
  )
}

function ChartContents({ months, empty }: CollectionsChartProps & { empty: boolean }) {
  const values = months.map(({ amount_cents }) => amount_cents)
  const yScale = buildYScale(values)
  const baselineY = empty ? PLOT_BOTTOM : yScale(0)
  const slotWidth = CHART_WIDTH / months.length
  const barWidth = slotWidth * 0.4
  const directLabelIndexes = new Set([values.indexOf(Math.max(...values)), months.length - 1])

  return (
    <>
      <line
        className="stroke-border"
        data-testid="collections-baseline"
        x1="0"
        x2={CHART_WIDTH}
        y1={baselineY}
        y2={baselineY}
      />
      {empty ? (
        <text
          className="fill-muted-foreground text-caption"
          textAnchor="middle"
          x={CHART_WIDTH / 2}
          y={(PLOT_TOP + PLOT_BOTTOM) / 2}
        >
          No collections yet.
        </text>
      ) : (
        months.map((month, index) => (
          <ChartBar
            key={month.starts_on}
            barWidth={barWidth}
            directLabel={directLabelIndexes.has(index)}
            index={index}
            month={month}
            slotWidth={slotWidth}
            yScale={yScale}
          />
        ))
      )}
    </>
  )
}

interface ChartBarProps {
  month: CollectionMonth
  index: number
  slotWidth: number
  barWidth: number
  directLabel: boolean
  yScale: (value: number) => number
}

function ChartBar({ month, index, slotWidth, barWidth, directLabel, yScale }: ChartBarProps) {
  const x = index * slotWidth + (slotWidth - barWidth) / 2
  const zeroY = yScale(0)
  const valueY = yScale(month.amount_cents)
  const y = Math.min(zeroY, valueY)
  const height = Math.max(Math.abs(zeroY - valueY), 1)
  const directLabelY = month.amount_cents < 0 ? y + height + 14 : y - 7

  return (
    <g>
      <title>{`${month.label} · ${month.amount}`}</title>
      {month.amount_cents !== 0 && (
        <path
          aria-label={`${month.label}: ${month.amount}`}
          className="fill-primary"
          d={barPath(x, barWidth, zeroY, valueY)}
          data-testid="collections-bar"
        />
      )}
      {directLabel && (
        <text
          className="fill-foreground text-caption"
          data-testid="collections-direct-label"
          textAnchor="middle"
          x={x + barWidth / 2}
          y={directLabelY}
        >
          {month.amount}
        </text>
      )}
      <text
        className="fill-muted-foreground text-caption"
        textAnchor="middle"
        x={index * slotWidth + slotWidth / 2}
        y={LABEL_Y}
      >
        {month.label.slice(0, 1)}
      </text>
    </g>
  )
}

function barPath(x: number, width: number, zeroY: number, valueY: number) {
  const radius = Math.min(4, Math.abs(zeroY - valueY))
  const roundedY = valueY + (valueY < zeroY ? radius : -radius)
  const direction = valueY < zeroY ? 1 : -1
  return [
    `M ${x} ${zeroY}`,
    `V ${roundedY}`,
    `Q ${x} ${valueY} ${x + radius} ${valueY}`,
    `H ${x + width - radius}`,
    `Q ${x + width} ${valueY} ${x + width} ${valueY + radius * direction}`,
    `V ${zeroY}`,
    'Z',
  ].join(' ')
}

function buildYScale(values: number[]) {
  const minimum = Math.min(0, ...values)
  const maximum = Math.max(0, ...values)
  const range = Math.max(maximum - minimum, 1)
  const plotHeight = PLOT_BOTTOM - PLOT_TOP

  return (value: number) => PLOT_TOP + ((maximum - value) / range) * plotHeight
}
